import { useCallback, useEffect, useMemo, useState } from "react";
import logger from "utils/logger";
import { getCategories, getExercises } from "features/planner/api/exerciseApi";

const isAbortError = (err) => err?.name === "AbortError";

export default function useExerciseCatalog() {
  const [exercises, setExercises] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategories, setActiveCategories] = useState([]);
  const [isCategoriesLoading, setIsCategoriesLoading] = useState(true);
  const [isExercisesLoading, setIsExercisesLoading] = useState(true);
  const [catalogError, setCatalogError] = useState(null);

  useEffect(() => {
    const abortController = new AbortController();

    setIsCategoriesLoading(true);
    setCatalogError(null);

    getCategories(abortController.signal)
      .then((data) => {
        if (abortController.signal.aborted) return;

        setCategories(data);
        setIsCategoriesLoading(false);
      })
      .catch((err) => {
        if (abortController.signal.aborted || isAbortError(err)) return;

        logger.error("Failed to fetch categories", err);
        setCatalogError(err);
      });

    return () => abortController.abort();
  }, []);

  useEffect(() => {
    const abortController = new AbortController();

    setIsExercisesLoading(true);
    setCatalogError(null);

    getExercises(activeCategories, abortController.signal)
      .then((data) => {
        if (abortController.signal.aborted) return;

        setExercises(data);
        setIsExercisesLoading(false);
      })
      .catch((err) => {
        if (abortController.signal.aborted || isAbortError(err)) return;

        logger.error("Failed to fetch exercises", err);
        setCatalogError(err);
      });

    return () => abortController.abort();
  }, [activeCategories]);

  const clearActiveCategories = useCallback(() => {
    setActiveCategories([]);
  }, []);

  const toggleCategory = useCallback(
    (catId) => {
      if (catId === null) {
        clearActiveCategories();
        return;
      }

      setActiveCategories((previousCategories) => {
        if (previousCategories.includes(catId)) {
          return previousCategories.filter((id) => id !== catId);
        }
        return [...previousCategories, catId];
      });
    },
    [clearActiveCategories]
  );

  const isCatalogLoading = isCategoriesLoading || isExercisesLoading;

  return useMemo(
    () => ({
      exercises,
      categories,
      activeCategories,
      toggleCategory,
      clearActiveCategories,
      isCategoriesLoading,
      isCatalogLoading,
      catalogError,
    }),
    [
      activeCategories,
      catalogError,
      categories,
      clearActiveCategories,
      exercises,
      isCategoriesLoading,
      isCatalogLoading,
      toggleCategory,
    ]
  );
}
